import {
  Children,
  CSSProperties,
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { exportKimTableToXlsx } from '../../utils/export-kim-table';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

// Above this many body rows, the table defaults to collapsed
const ROW_LIMIT = 50;

// Finds the first direct child element rendered as the given HTML tag (html-react-parser
// produces elements whose `type` is the lowercase tag name).
function findElementByTag(children: ReactNode, tag: string): ReactElement | undefined {
  return Children.toArray(children).find((child): child is ReactElement => isValidElement(child) && child.type === tag);
}

const ROW_NUMBER_CLASSES =
  '[&_tbody]:[counter-reset:kim-row] [&_tbody_tr]:[counter-increment:kim-row] [&_th:first-child]:relative [&_td:first-child]:relative [&_th:first-child]:pl-[calc(var(--kim-gutter)+0.75rem)] [&_td:first-child]:pl-[calc(var(--kim-gutter)+0.75rem)] [&_td:first-child]:before:content-[counter(kim-row)] [&_td:first-child]:before:absolute [&_td:first-child]:before:inset-y-0 [&_td:first-child]:before:left-0 [&_td:first-child]:before:pl-3 [&_td:first-child]:before:w-[var(--kim-gutter)] [&_td:first-child]:before:justify-center [&_td:first-child]:before:border-r [&_td:first-child]:before:border-gray-200 [&_td:first-child]:before:bg-gray-50 [&_td:first-child]:before:pt-2.25 [&_td:first-child]:before:text-gray-400 [&_td:first-child]:before:tabular-nums [&_th:first-child]:before:absolute [&_th:first-child]:before:inset-y-0 [&_th:first-child]:before:left-0 [&_th:first-child]:before:w-[var(--kim-gutter)] [&_th:first-child]:before:border-r [&_th:first-child]:before:border-gray-200 [&_th:first-child]:before:bg-gray-50 [&_th:first-child]:before:content-[""]';

const TABLE_CELL_CLASSES =
  'w-full border-separate border-spacing-0 [&_th:not(:first-child)]:border-l [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_th]:px-3 [&_th]:text-gray-600 [&_th]:whitespace-nowrap [&_th]:py-2.25 [&_th]:text-left [&_th]:font-normal [&_td]:border-t [&_td:not(:first-child)]:border-l [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-2.25 [&_td]:align-top [&_thead_tr:first-child_th:first-child]:rounded-tl-md [&_thead_tr:first-child_th:last-child]:rounded-tr-md [&_tbody_tr:last-child_td:first-child]:rounded-bl-md [&_tbody_tr:last-child_td:last-child]:rounded-br-md';

export function KimTable({ children, hideActions = false }: { children: ReactNode; hideActions?: boolean }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [rowsExpanded, setRowsExpanded] = useState(false);
  const [pendingExport, setPendingExport] = useState(false);
  const tableRef = useRef<HTMLTableElement>(null);

  // Rows arrive as a parsed React element tree (<thead>/<tbody>) -- locate the
  // <tbody> so we can render only the first ROW_LIMIT rows when collapsed
  const theadEl = findElementByTag(children, 'thead');
  const tbodyEl = findElementByTag(children, 'tbody');
  const allRows = tbodyEl
    ? Children.toArray(tbodyEl.props.children).filter((child) => isValidElement(child) && child.type === 'tr')
    : [];
  const totalRows = allRows.length;
  const canCollapse = Boolean(tbodyEl) && totalRows > ROW_LIMIT;
  const showAll = !canCollapse || rowsExpanded;
  const visibleRows = showAll ? allRows : allRows.slice(0, ROW_LIMIT);

  // Size the row-number gutter to the widest number so 10,000+ rows don't clip.
  // Measured from the rendered DOM since a pseudo-element can't auto-size to its counter.
  const [gutterDigits, setGutterDigits] = useState(2);
  useLayoutEffect(() => {
    const rowCount = Array.from(tableRef.current?.tBodies ?? []).reduce((total, body) => total + body.rows.length, 0);
    setGutterDigits(Math.max(2, String(rowCount).length));
  }, [children, rowsExpanded, totalRows]);

  // Export reads the live DOM, so a collapsed table must render all its rows first. Expand, then
  // export once the extra rows have mounted, and re-collapse afterwards so the export leaves the
  // table as the user had it.
  const handleExport = () => {
    if (!tableRef.current) return;
    if (showAll) {
      exportKimTableToXlsx(tableRef.current);
    } else {
      setPendingExport(true);
      setRowsExpanded(true);
    }
  };
  useEffect(() => {
    if (pendingExport && showAll && tableRef.current) {
      exportKimTableToXlsx(tableRef.current);
      setPendingExport(false);
      setRowsExpanded(false);
    }
  }, [pendingExport, showAll]);

  return (
    <div className="not-prose relative -mx-2 my-5 w-[calc(100%+1rem)] max-w-[calc(100%+1rem)] min-w-0 font-sans text-xs">
      {!hideActions && (
        <div className="flex items-center justify-end gap-1 px-2 pb-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Export table as Excel"
            onClick={handleExport}
            className="px-1"
          >
            <Icon className="lucide--arrow-up-right" />
            Export to Excel
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={isExpanded ? 'Collapse table' : 'Expand table'}
            onClick={() => setIsExpanded((expanded) => !expanded)}
            className="px-1"
          >
            <Icon className={cn(isExpanded ? 'lucide--fold-horizontal' : 'lucide--unfold-horizontal')} />
            {isExpanded ? 'Contract' : 'Expand'}
          </Button>
        </div>
      )}
      <div
        className={cn(
          'min-w-0 overflow-x-auto rounded-md border border-gray-200 bg-white',
          isExpanded ? 'relative left-1/2 w-[100cqw] max-w-[100cqw] -translate-x-1/2' : 'w-full max-w-full',
          canCollapse && !rowsExpanded && 'mask-b-from-[calc(100%-12rem)] mask-b-to-[calc(100%-1rem)]',
        )}
      >
        <table
          ref={tableRef}
          className={cn(TABLE_CELL_CLASSES, ROW_NUMBER_CLASSES)}
          style={{ '--kim-gutter': `calc(${gutterDigits}ch + 1.5rem)` } as CSSProperties}
        >
          {tbodyEl ? (
            <>
              {theadEl}
              {cloneElement(tbodyEl, {}, visibleRows)}
            </>
          ) : (
            children
          )}
        </table>
      </div>

      {canCollapse && !rowsExpanded && (
        <button
          type="button"
          onClick={() => setRowsExpanded(true)}
          className="group absolute bottom-0 flex w-full justify-center pb-3 focus:outline-none"
        >
          <div className="flex items-center gap-0.75 rounded-full border bg-white py-1 pr-1.5 pl-2.5 font-sans text-xs text-gray-500 shadow-lg/2 ring-teal-500 ring-offset-2 ring-offset-transparent transition-colors duration-150 group-hover:border-gray-300 group-hover:bg-gray-50 group-hover:text-gray-600 group-focus-visible:ring-1 group-focus-visible:ring-offset-white">
            <span>Show {totalRows - ROW_LIMIT} more rows</span>
            <Icon className="lucide--chevron-down mt-px text-sm" />
          </div>
        </button>
      )}

      {canCollapse && rowsExpanded && (
        <div className="flex justify-center pt-4">
          <Button type="button" variant="ghost" size="sm" onClick={() => setRowsExpanded(false)} className="px-2">
            <Icon className="lucide--chevron-up" />
            Show fewer rows
          </Button>
        </div>
      )}
    </div>
  );
}
